import {
  createReplayImageLayer,
  createReplayVideoLayer,
  findReplayGroups,
} from '@picsart/web-replay-sdk/context';
import { generateUUID } from '@picsart/design-system/utils/helpers';

import { getContext } from '@/context';
import { PICSART_APP_URL } from '@/consts/urls';
import { PreviewMediaType } from '@/types';

type AddMediaToEditorParams = {
  mediaUrl: string;
  mediaType?: PreviewMediaType;
};

type VideoMetadata = {
  width: number;
  height: number;
  duration: number;
};

/**
 * Load image and get its dimensions
 */
const loadImage = (src: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Extract video metadata (width, height, duration)
 */
const extractVideoMetadata = (videoUrl: string): Promise<VideoMetadata> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';

    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
    };

    video.onerror = () => reject(new Error('Failed to load video metadata'));
    video.src = videoUrl;
  });
};

/**
 * Adds image to the Picsart editor canvas using scene.updateReplay (preset-gens approach).
 * Uses SDK utilities for creating replay layers.
 */
const addImageToCanvas = async ({ src }: { src: string }): Promise<void> => {
  const { width, height } = await loadImage(src);

  const layer = createReplayImageLayer({
    name: 'PHOTO',
    uuid: generateUUID(),
    locked: false,
    mediaType: 'PHOTO',
    props: {
      hidden: false,
      opacity: 1,
      x: 0,
      y: 0,
      image: {
        width,
        height,
        location: src,
        type: 'photo',
        id: generateUUID(),
      },
      scaleX: 1,
      scaleY: 1,
    },
  });

  const context = getContext();

  context.scene.updateReplay((draftReplay) => {
    const groups = findReplayGroups(draftReplay);

    const canvasGroup = groups.filter((group) =>
      context.scene.hasLayerMediaType(group, 'CANVAS_GROUP')
    )[0];

    canvasGroup.props.children.value.push(layer.uuid);
    draftReplay.context.layers[layer.uuid] = layer;
  });

  await context.scene.sync();
};

/**
 * Adds video to the Picsart editor canvas using scene.updateReplay (preset-gens approach).
 * Uses SDK utilities for creating replay layers.
 */
const addVideoToCanvas = async ({
  videoUrl,
}: {
  videoUrl: string;
}): Promise<void> => {
  const context = getContext();
  const { duration, width, height } = await extractVideoMetadata(videoUrl);
  const resourceId = generateUUID();

  const replayVideoLayer = createReplayVideoLayer({
    name: 'VIDEO',
    mediaType: 'VIDEO',
    uuid: generateUUID(),
    locked: false,
    meta: {
      currentTime: 0,
      duration,
      isMuted: false,
      isPlaying: false,
      isPremium: false,
      resourceId,
      resource: {
        volume: 100,
        type: 'VIDEO',
        uri: videoUrl,
        guid: resourceId,
        trimStart: 0,
        trimEnd: duration,
        original_width: width,
        origial_height: height,
      },
    },
    props: {
      x: 0,
      y: 0,
      scaleX: 1,
      scaleY: 1,
      speed: 1,
      repeat: false,
      rotation: 0,
      hidden: false,
      opacity: 1,
      duration,
      muted: true,
      volume: 100,
      trimStart: 0,
      trimEnd: duration,
      video: {
        width,
        height,
        location: videoUrl,
        type: 'video',
        id: resourceId,
      },
    },
  });

  context.scene.updateReplay((draftReplay) => {
    const groups = findReplayGroups(draftReplay);
    const canvasGroup = groups.filter(
      (group) => group.mediaType === 'CANVAS_GROUP'
    )[0];

    if (canvasGroup) {
      canvasGroup.props.duration.value = Math.max(
        duration,
        canvasGroup.props.duration.value || 0
      );
      canvasGroup.props.children.value.push(replayVideoLayer.uuid);
    }

    draftReplay.context.layers[replayVideoLayer.uuid] = replayVideoLayer;
  });

  await context.scene.sync();
};

/**
 * Adds media to the Picsart editor canvas.
 * Uses scene.updateReplay for both images and videos (preset-gens approach).
 */
export const addMediaToEditor = async ({
  mediaUrl,
  mediaType = PreviewMediaType.IMAGE,
}: AddMediaToEditorParams): Promise<void> => {
  try {
    if (mediaType === PreviewMediaType.VIDEO) {
      await addVideoToCanvas({ videoUrl: mediaUrl });
    } else {
      await addImageToCanvas({ src: mediaUrl });
    }
  } catch {
    // Context not available or canvas operation failed
  }
};

/**
 * Opens media in the Picsart editor (new window/tab on web, deeplink on native).
 * - Web: Opens editor URL in same window with media as query param
 * - Native (iOS/Android): Opens native editor via deeplink picsart://editor?photo-url=
 */
export const openMediaInEditor = async ({
  mediaUrl,
  mediaType = PreviewMediaType.IMAGE,
}: AddMediaToEditorParams): Promise<void> => {
  const encodedUrl = encodeURIComponent(mediaUrl);

  const webQueryParam =
    mediaType === PreviewMediaType.VIDEO
      ? PreviewMediaType.VIDEO
      : PreviewMediaType.IMAGE;
  const webUrl = `${PICSART_APP_URL}/create/editor?${webQueryParam}=${encodedUrl}`;

  window.open(webUrl, '_blank', 'noopener,noreferrer');
};

export type { AddMediaToEditorParams };
